/**
 * Logos prop firm bundlés localement (`src/assets/logos/<slug>/...`) — un
 * favicon distant est un point de panne (site down, CORS, latence) pour un
 * simple avatar affiché partout dans le cockpit.
 *
 * `logo-bg.svg` = logo blanc + tuile couleur de marque, exactement comme
 * propfirmmatch.com l'affiche : une seule variante suffit pour les deux thèmes
 * (dark/light), pas besoin de switcher `logo.svg` selon le thème actif. Certaines
 * firms n'ont pas (encore) reçu ce traitement et n'ont qu'un `logo.svg` seul ou
 * un raster (`logo.webp`/`.png`/`.jpg`) — on retombe dessus plutôt que de ne rien
 * afficher. Clé = slug de la firm, qui EST l'id du document Firestore `propFirms/{slug}`.
 */
const bgSvgs = import.meta.glob('../../assets/logos/*/logo-bg.svg', { eager: true, as: 'url' });
const plainSvgs = import.meta.glob('../../assets/logos/*/logo.svg', { eager: true, as: 'url' });
const webps = import.meta.glob('../../assets/logos/*/logo.webp', { eager: true, as: 'url' });
const pngs = import.meta.glob('../../assets/logos/*/logo.png', { eager: true, as: 'url' });
const jpgs = import.meta.glob('../../assets/logos/*/logo.jpg', { eager: true, as: 'url' });

function indexBySlug(modules: Record<string, unknown>, filename: string): Record<string, string> {
  const bySlug: Record<string, string> = {};
  const pattern = new RegExp(`assets/logos/([^/]+)/${filename}$`);
  for (const [filePath, url] of Object.entries(modules)) {
    const match = filePath.match(pattern);
    if (match) bySlug[match[1]] = url as string;
  }
  return bySlug;
}

const BG_SVG = indexBySlug(bgSvgs, 'logo-bg\\.svg');
const PLAIN_SVG = indexBySlug(plainSvgs, 'logo\\.svg');
const WEBP = indexBySlug(webps, 'logo\\.webp');
const PNG = indexBySlug(pngs, 'logo\\.png');
const JPG = indexBySlug(jpgs, 'logo\\.jpg');

/**
 * Firms dont la tuile `logo-bg.svg` ne se lit pas bien dans l'app (fond blanc
 * plein sur un logo déjà coloré, par ex.) : on préfère leur marque brute
 * (`logo.svg`, transparent) plutôt que la tuile générée automatiquement.
 */
const PREFER_PLAIN_LOGO = new Set(['aquafunded', 'aquafutures']);

/** Logo local d'une firm, ou `null` si elle n'a aucun asset local. */
export function getLocalPropFirmLogo(firmId: string): string | null {
  if (PREFER_PLAIN_LOGO.has(firmId) && PLAIN_SVG[firmId]) return PLAIN_SVG[firmId];
  return BG_SVG[firmId] ?? PLAIN_SVG[firmId] ?? WEBP[firmId] ?? PNG[firmId] ?? JPG[firmId] ?? null;
}
